import { useEffect, useRef, useState } from 'react'

import {
  type CarrierRequestService,
  carrierRequestServiceOptions,
} from '#hooks/useCarrierRequestFilters'

interface CarrierRequestServiceMultiSelectProps {
  value: CarrierRequestService[]
  onApply: (services: CarrierRequestService[]) => void
}

const allServiceValues = carrierRequestServiceOptions.map(
  option => option.value,
)

const getServiceTriggerLabel = (labels: string[]): string => {
  if (labels.length === 0) return 'Select services'
  if (labels.length <= 2) return labels.join(', ')
  return `${labels.slice(0, 2).join(', ')} +${labels.length - 2} more`
}

const getNextServices = (
  current: CarrierRequestService[],
  toggled: CarrierRequestService,
): CarrierRequestService[] => {
  const next = current.includes(toggled)
    ? current.filter(v => v !== toggled)
    : [...current, toggled]

  return next.length === 0 ? ['tracking'] : next
}

const CarrierRequestServiceMultiSelect = ({
  value,
  onApply,
}: CarrierRequestServiceMultiSelectProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const [draftServices, setDraftServices] =
    useState<CarrierRequestService[]>(value)
  const containerRef = useRef<HTMLDivElement | null>(null)

  const isAllSelected = allServiceValues.every(v => value.includes(v))
  const selectedServiceLabels = carrierRequestServiceOptions
    .filter(option => value.includes(option.value))
    .map(option => option.label)
  const serviceTriggerLabel = getServiceTriggerLabel(selectedServiceLabels)

  useEffect(() => {
    if (!isOpen) {
      setDraftServices(value)
    }
  }, [isOpen, value])

  useEffect(() => {
    if (!isOpen) {
      return
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen])

  const toggleService = (service: CarrierRequestService) => {
    setDraftServices(current => getNextServices(current, service))
  }

  return (
    <div className="relative flex-1" ref={containerRef}>
      <label className="form-control">
        <div className="label">
          <span className="label-text">Services</span>
        </div>
        <button
          type="button"
          className="btn btn-outline min-h-12 w-full justify-between normal-case font-normal"
          onClick={() => {
            setDraftServices(value)
            setIsOpen(open => !open)
          }}
          aria-expanded={isOpen}
          aria-haspopup="dialog"
        >
          <span className="truncate text-left">{serviceTriggerLabel}</span>
          <span className="badge badge-neutral badge-sm">
            {isAllSelected ? 'All' : `${selectedServiceLabels.length} selected`}
          </span>
        </button>
      </label>
      {isOpen && (
        <div className="absolute left-0 right-0 top-full z-20 mt-2 rounded-box border border-base-300 bg-base-100 p-4 shadow-2xl">
          <div className="mb-3 flex items-center justify-between gap-3">
            <span className="text-sm text-base-content/70">
              Choose one or more services
            </span>
            <button
              type="button"
              className="btn btn-ghost btn-xs"
              onClick={() => setDraftServices(allServiceValues)}
            >
              Select all
            </button>
          </div>
          <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
            {carrierRequestServiceOptions.map(option => (
              <label
                key={option.value}
                className="label cursor-pointer justify-start gap-3 rounded-box border border-base-300 px-3 py-2"
              >
                <input
                  type="checkbox"
                  className="checkbox checkbox-sm"
                  checked={draftServices.includes(option.value)}
                  onChange={() => toggleService(option.value)}
                />
                <span className="label-text">{option.label}</span>
              </label>
            ))}
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => setIsOpen(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() => {
                onApply(draftServices)
                setIsOpen(false)
              }}
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export { CarrierRequestServiceMultiSelect }
